/**
 * FocusIndicator - Visual indicator for focused panel
 */

import React from 'react';

export interface FocusIndicatorProps {
  /** Whether this panel is currently focused */
  isFocused: boolean;

  /** Custom class name */
  className?: string;

  /** Custom styles */
  style?: React.CSSProperties;
}

/**
 * Visual indicator that highlights the focused panel
 *
 * Renders a subtle border/glow around the focused panel for keyboard navigation feedback
 */
export const FocusIndicator: React.FC<FocusIndicatorProps> = ({
  isFocused,
  className = '',
  style = {},
}) => {
  if (!isFocused) {
    return null;
  }

  const defaultStyles: React.CSSProperties = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    pointerEvents: 'none',
    border: '2px solid var(--panel-focus-border-color, #0066cc)',
    borderRadius: '4px',
    boxShadow: '0 0 0 1px var(--panel-focus-shadow-color, rgba(0, 102, 204, 0.2))',
    zIndex: 1000,
    transition: 'opacity 0.15s ease-in-out',
    ...style,
  };

  return (
    <div
      className={`panel-focus-indicator ${className}`}
      style={defaultStyles}
      role="presentation"
      aria-hidden="true"
    />
  );
};
